<template>
  <div class="cat-proyecto">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-proyecto-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${proyectos.length} registros`"
          @new="addProyecto"
          @refresh="loadProyectos"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando proyectos...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!proyectos || proyectos.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay proyectos. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && proyectos && proyectos.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="proyectos"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar proyectos..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot personalizado para la columna EsActivo -->
            <template #cell-EsActivo="{ value }">
              <span v-if="value" class="badge bg-success">Sí</span>
              <span v-else class="badge bg-secondary">No</span>
            </template>
            
            <!-- Slot personalizado para las fechas -->
            <template #cell-FechaInicio="{ value }">
              <span v-if="value">{{ formatDate(value) }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <template #cell-FechaFin="{ value }">
              <span v-if="value">{{ formatDate(value) }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <!-- Slot personalizado para los montos -->
            <template #cell-Presupuesto="{ value }">
              <span v-if="value !== null && value !== undefined">{{ formatCurrency(value) }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <template #cell-CostoActual="{ value }">
              <span v-if="value !== null && value !== undefined">{{ formatCurrency(value) }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <template #cell-PorcentajeAvance="{ value }">
              <span v-if="value !== null && value !== undefined">{{ formatNumber(value) }}%</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editProyecto(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar proyecto"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave, row.EmpresaId)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar proyecto"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Proyecto -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="proyectoModal" 
      tabindex="-1" 
      aria-labelledby="proyectoModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="proyectoModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Proyecto' : 'Nuevo Proyecto' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatProyectoForm
              :proyecto="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatProyectoService from '../controllers/CatProyectoController.js';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatProyectoForm from '../components/CatProyectoForm.vue';

export default {
  name: 'CatProyectoView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatProyectoForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: null,
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: 0,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        Descripcion: '',
        FechaInicio: '',
        FechaFin: '',
        Cliente: '',
        Responsable: '',
        Direccion: '',
        Telefono: '',
        Email: '',
        Presupuesto: 0,
        CostoActual: 0,
        PorcentajeAvance: 0,
        Estatus: '',
        Notas: ''
      }
    };
  },
  computed: {
    proyectos() {
      return this.service ? this.service.proyectos : [];
    },
    loading() {
      return this.service ? this.service.loading : false;
    },
    error() {
      return this.service ? this.service.error : null;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'Empresa ID', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { 
          key: 'EsActivo', 
          label: 'Activo', 
          sortable: true,
          format: (value) => value ? 'Sí' : 'No'
        },
        { key: 'Descripcion', label: 'Descripción', sortable: true },
        { key: 'FechaInicio', label: 'Fecha Inicio', sortable: true },
        { key: 'FechaFin', label: 'Fecha Fin', sortable: true },
        { key: 'Cliente', label: 'Cliente', sortable: true },
        { key: 'Responsable', label: 'Responsable', sortable: true },
        { key: 'Direccion', label: 'Dirección', sortable: true },
        { key: 'Telefono', label: 'Teléfono', sortable: true },
        { key: 'Email', label: 'Email', sortable: true },
        { key: 'Presupuesto', label: 'Presupuesto', sortable: true },
        { key: 'CostoActual', label: 'Costo Actual', sortable: true },
        { key: 'PorcentajeAvance', label: 'Avance %', sortable: true },
        { key: 'Estatus', label: 'Estatus', sortable: true },
        { key: 'Notas', label: 'Notas', sortable: true }
      ];
    },
    // Verificar si el usuario puede crear proyectos
    canCreate() {
      return this.canAccess;
    }
  },
  created() {
    this.service = new CatProyectoService();
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadProyectos() {
      console.log('Cargando proyectos...');
      if (this.service) {
        await this.service.listar();
      }
    },
    addProyecto() {
      this.isEditing = false;
      this.openModal();
    },
    async editProyecto(proyecto) {
      this.isEditing = true;
      try {
        // Cargar datos completos del proyecto
        if (this.service) {
          const response = await this.service.lectura(proyecto.Clave, proyecto.EmpresaId);
          if (response && response.data) {
            this.formData = { ...response.data };
          } else {
            this.formData = { ...proyecto };
          }
        } else {
          this.formData = { ...proyecto };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar proyecto:', error);
        this.$alert.error('No se pudo cargar el proyecto para edición.');
      }
    },
    confirmDelete(clave, empresaId) {
      console.log('Iniciando confirmación de eliminación para:', clave, empresaId);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el proyecto con clave "${clave}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteProyecto(clave, empresaId);
        }
      });
    },
    async deleteProyecto(clave, empresaId) {
      console.log('Ejecutando eliminación para:', clave, empresaId);
      
      try {
        if (this.service) {
          const response = await this.service.borrar(clave, empresaId);
          console.log('Respuesta de eliminación recibida:', response);
          
          // Verificar si la respuesta es exitosa
          if (response && response.success) {
            // Mostrar mensaje de éxito
            this.$alert.success(response.message || 'Proyecto eliminado exitosamente');
            // Recargar datos
            this.service.recargar();
          } else {
            // Si no es exitosa, mostrar error
            this.$alert.error(response.message || 'No se pudo eliminar el proyecto. Por favor, intente nuevamente.');
          }
        }
      } catch (error) {
        console.error('Error al eliminar proyecto:', error);
        this.$alert.error('No se pudo eliminar el proyecto. Por favor, intente nuevamente.');
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: this.service && this.service.selectedEmpresa ? this.service.selectedEmpresa.EmpresaId : 0,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        Descripcion: '',
        FechaInicio: '',
        FechaFin: '',
        Cliente: '',
        Responsable: '',
        Direccion: '',
        Telefono: '',
        Email: '',
        Presupuesto: 0,
        CostoActual: 0,
        PorcentajeAvance: 0,
        Estatus: '',
        Notas: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        if (this.service) {
          const response = await this.service.guardar(formData);
          
          // Verificar si la respuesta es exitosa
          if (response && response.success) {
            // Mostrar mensaje de éxito
            this.$alert.success(response.message || 'Proyecto guardado exitosamente');
            // Cerrar modal y recargar datos
            this.closeModal();
            this.service.recargar();
          } else {
            // Si no es exitosa, mostrar error
            this.$alert.error(response.message || 'No se pudo guardar el proyecto. Por favor, intente nuevamente.');
          }
        }
      } catch (error) {
        console.error('Error al guardar proyecto:', error);
        this.$alert.error('No se pudo guardar el proyecto. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    },
    async downloadBulk() {
      try {
        if (this.service) {
          await this.service.downloadBulk();
          this.$alert.success('Descarga iniciada');
        }
      } catch (error) {
        console.error('Error descargando proyectos:', error);
        this.$alert.error('No se pudo descargar los proyectos. Por favor, intente nuevamente.');
      }
    },
    // Métodos de formateo
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      }).format(value);
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      return parseFloat(value).toFixed(2);
    }
  }
}
</script>

<style scoped>
.cat-proyecto {
  min-height: calc(100vh - 200px);
}

.cat-proyecto-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
